/**
 * SSR/Hydration utilities for Stencil components
 *
 * These utilities help with parsing props during client-side hydration
 * when complex data structures are passed as JSON strings in HTML attributes.
 */

/**
 * Parse any JSON prop from HTML attribute string during client-side hydration.
 * Works with objects, arrays, and primitives.
 *
 * This function solves the SSR hydration issue where:
 * - Server passes complex props as objects/arrays
 * - HTML attributes can only contain strings
 * - Client receives JSON strings that need to be parsed back to objects
 *
 * Key features:
 * - Only runs on client-side (Build.isBrowser check enables tree-shaking for SSR bundle)
 * - Handles HTML-encoded quotes (&quot; → ")
 * - Type-safe: accepts both object/array and string types
 * - Fails gracefully: returns a fallback value or undefined if parsing fails
 *
 * @param value - Prop value (parsed on server, string on client)
 * @param fallback - Fallback value if parsing fails
 * @returns Parsed value or fallback
 *
 * @example
 * ```tsx
 * import { parseJsonProp } from '../../utils/helpers/hydration';
 *
 * @Component({ tag: 'my-component' })
 * export class MyComponent {
 *   @Prop({ mutable: true }) items?: Array<{ title: string }> | string;
 *   @Prop({ mutable: true }) config?: Record<string, any> | string;
 *   @Prop({ mutable: true }) constants?: Record<string, string> | string;
 *
 *   componentWillLoad() {
 *     this.items = parseJsonProp(this.items, []);
 *     this.config = parseJsonProp(this.config);
 *     this.constants = parseJsonProp(this.constants);
 *   }
 * }
 * ```
 */
export function parseJsonProp<T>(value: T | string | undefined, fallback?: T): T | undefined {
  // If value is undefined/null, return fallback
  if (value === undefined || value === null) {
    return fallback;
  }

  // If value is not a string (already parsed object/array), return as is
  if (typeof value !== 'string') {
    return value as T;
  }

  // Parse JSON string (works on both server and client)
  try {
    // Decode HTML entities (&quot; → ")
    const decoded = value.replace(/&quot;/g, '"');
    const parsed = JSON.parse(decoded);
    return parsed as T;
  } catch {
    // Return fallback on parse error
    return fallback;
  }
}
